<!-- @format -->
<template>
    <app-widget
        ref="widget"
        :widget="widget"
        :loading="isLoading"
        class="registered-deal-widget"
        content-class="registered-deal-widget-content"
    >
        <template #headerTitle>
            <h3>{{ headerTitle }}</h3>
        </template>
        <template #content>
            <registered-deal-widget-details
                :$v="$v"
                :submitted="submitted"
                :registered-deal="registeredDeal"
                :mdf-requests="mdfRequests"
                :is-adding="isAdding"
                :is-editing="isEditing"
                :is-client="isClient"
                :client-id="clientId"
                @update-name="updateName"
                @update-agency="updateAgency"
                @update-fund-request="updateFundRequest"
                @update-registration-number="updateRegistrationNumber"
                @update-close-date="updateCloseDate"
                @update-description="updateDescription"
            />
            <template v-if="isEditing && !isClient">
                <div class="divider"></div>
                <registered-deal-widget-status
                    :$v="$v"
                    :registered-deal="registeredDeal"
                    :is-editing="isEditing"
                    @update-is-approved="updateIsApproved"
                    @update-reason="updateReason"
                />
            </template>
            <div class="divider"></div>
            <registered-deal-widget-amount-solutions
                :$v="$v"
                :submitted="submitted"
                :registered-deal="registeredDeal"
                :agency-solutions="agencySolutions"
                :is-client="isClient"
                :is-editing="isEditing"
                :is-loading-agency-solutions="isLoadingAgencySolutions"
                @update-amount="updateAmount"
                @add-solution="addSolution"
                @delete-solution="deleteSolution"
                @update-solution-id="updateSolutionId"
                @update-solution-unit-count="updateSolutionUnitCount"
            />
        </template>

        <template #footer>
            <div>
                <app-button class="back-button" @click="close">{{ closeButtonLabel }}</app-button>
            </div>
            <div>
                <template v-if="!isLoading">
                    <app-button v-if="isEditing" :disabled="isSaving" @click="save"
                        >Save</app-button
                    >
                    <app-button v-else @click="edit">Edit</app-button>
                </template>
            </div>
        </template>
    </app-widget>
</template>

<script>
import {
    REGISTERED_DEAL_WIDGET_ATTEMPT_SAVE_EVENT,
    REGISTERED_DEAL_WIDGET_CONTEXT,
    REGISTERED_DEAL_WIDGET_NAME,
} from '@/components/sliding-widgets/registered-deal-widget/registered-deal-widget';
import EventBus from '@/scripts/event-bus';
import { RegisteredDealWidgetService } from '@/components/sliding-widgets/registered-deal-widget/registered-deal-widget-service';
import { required, minValue } from 'vuelidate/lib/validators';
import { solutionIsPublished } from './registered-deal-widget-validators';
const AppButton = () => import('@/components/common/app-button.vue');
const AppWidget = () => import('@/components/common/app-widget/app-widget.vue');
const RegisteredDealWidgetDetails = () =>
    import(
        '@/components/sliding-widgets/registered-deal-widget/registered-deal-widget-details.vue'
    );
const RegisteredDealWidgetAmountSolutions = () =>
    import(
        '@/components/sliding-widgets/registered-deal-widget/registered-deal-widget-amount-solutions.vue'
    );
const RegisteredDealWidgetStatus = () =>
    import('@/components/sliding-widgets/registered-deal-widget/registered-deal-widget-status.vue');

export default {
    name: REGISTERED_DEAL_WIDGET_NAME,

    components: {
        'app-button': AppButton,
        'app-widget': AppWidget,
        'registered-deal-widget-details': RegisteredDealWidgetDetails,
        'registered-deal-widget-amount-solutions': RegisteredDealWidgetAmountSolutions,
        'registered-deal-widget-status': RegisteredDealWidgetStatus,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            isLoadingAgencySolutions: false,
            isLoadingMdfRequests: false,
            isSaving: false,
            editMode: false,
            submitted: false,
            agencyId: null,
            mdfRequests: [],
            agencySolutions: [],
            registeredDeal: {
                id: null,
                name: null,
                registrationNumber: null,
                fundRequestId: null,
                fundRequestName: null,
                isApproved: null,
                reason: null,
                description: null,
                amount: null,
                projectedCloseDate: null,
                agency: null,
                client: {
                    name: null,
                    imgUrl: null,
                },
                submittedByUser: {
                    name: null,
                    imgUrl: null,
                },
                company: {
                    name: null,
                    imgUrl: null,
                },
                primaryContact: {
                    name: null,
                    imgUrl: null,
                },
                opportunity: {
                    id: null,
                    name: null,
                    stage: {
                        name: null,
                        backgroundClass: null,
                    },
                },
                agencySolutions: [],
            },
        };
    },

    validations: {
        registeredDeal: {
            name: {
                required,
            },

            agency: {
                required,
            },

            amount: {
                required,
                minValue: minValue(0),
            },

            projectedCloseDate: {
                required,
            },

            agencySolutions: {
                $each: {
                    solutionIsPublished: function (solution) {
                        return solutionIsPublished(solution, this.agencySolutions);
                    },

                    solutionId: {
                        required,
                    },

                    unitCount: {
                        required,
                        minValue: minValue(1),
                    },
                },
            },
        },
    },

    computed: {
        registeredDealId() {
            return this.widget.registeredDealId;
        },

        context() {
            return this.widget.context;
        },

        clientId() {
            return this.registeredDeal?.client?.id ?? this.widget.clientId ?? null;
        },

        clientAccountId() {
            return this.widget.clientAccountId;
        },

        isAdding() {
            return this.widget.add === true;
        },

        isEditing() {
            return this.editMode === true || this.isAdding === true;
        },

        isClient() {
            return this.context === REGISTERED_DEAL_WIDGET_CONTEXT.CLIENT;
        },

        headerTitle() {
            if (this.editMode === true) {
                return 'Edit a Registered Deal';
            } else if (this.isAdding) {
                return 'Add a Registered Deal';
            }

            return 'Registered Deal Details';
        },

        closeButtonLabel() {
            return this.isEditing ? 'Cancel' : 'Close';
        },
    },

    async created() {
        this.editMode = this.widget.edit === true;
        this.agencyId = this.widget.agencyId;
        this.registeredDeal.opportunity.id = this.widget.opportunityId;

        await this.loadWidget();
    },

    methods: {
        async loadWidget() {
            try {
                this.isLoading = true;

                if (!this.isEditing) {
                    await this.loadDetailsWidget();
                } else if (this.isEditing && !this.isAdding) {
                    await this.loadEditWidget();
                } else if (this.isEditing && this.isAdding) {
                    await this.loadAddWidget();
                }
            } catch (error) {
                this.$helpers.showFailNotification('Failed to load the registered deal', { error });
                this.close();
            } finally {
                this.isLoading = false;
            }
        },

        async loadDetailsWidget() {
            await Promise.all([this.loadRegisteredDeal(), this.loadAgencySolutions()]);
        },

        async loadEditWidget() {
            await Promise.all([
                this.loadRegisteredDeal(),
                this.loadAgencySolutions(),
                this.loadMdfRequests(),
            ]);
        },

        async loadAddWidget() {
            await Promise.all([this.loadAgencySolutions()]);
        },

        async loadRegisteredDeal() {
            const registeredDeal = await RegisteredDealWidgetService.getRegisteredDealById(
                this.registeredDealId,
            );

            this.hydrateRegisteredDeal(registeredDeal);
        },

        async saveRegisteredDeal() {
            this.isSaving = true;

            try {
                if (this.isAdding) {
                    await RegisteredDealWidgetService.postRegisteredDeal(this.registeredDeal);
                    EventBus.$emit('registered-deal:update');
                    EventBus.$emit('opportunity:update');
                    this.$helpers.showSuccessNotification(
                        'Registered deal has been successfully added.',
                    );
                } else if (this.isEditing) {
                    await RegisteredDealWidgetService.patchRegisteredDeal(
                        this.registeredDeal.id,
                        this.registeredDeal,
                    );
                    EventBus.$emit('registered-deal:update');
                    EventBus.$emit('opportunity:update');
                    this.$helpers.showSuccessNotification(
                        'Registered deal has been successfully saved.',
                    );
                }
            } catch (error) {
                this.$helpers.showFailNotification('Failed to save the registered deal', { error });
            } finally {
                this.isSaving = false;
                this.close();
            }
        },

        async loadAgencySolutions() {
            if (this.agencyId == null || this.clientId == null) {
                return;
            }

            this.isLoadingAgencySolutions = true;

            const agencySolutions = await RegisteredDealWidgetService.getAgencySolutionsByAgencyId(
                this.agencyId,
                this.clientId,
            );
            this.agencySolutions = agencySolutions.map((agencySolution) => ({
                id: agencySolution?.Solution?.Id,
                name: agencySolution?.Solution?.Name,
            }));

            this.isLoadingAgencySolutions = false;
        },

        async loadMdfRequests() {
            if (this.clientAccountId == null || this.agencyId == null) {
                return;
            }

            this.isLoadingMdfRequests = true;

            const mdfRequests = await RegisteredDealWidgetService.getMdfRequests(
                this.clientAccountId,
                this.agencyId,
            );
            this.mdfRequests = mdfRequests.map((mdfRequest) => ({
                id: mdfRequest.Id,
                name: mdfRequest.Name,
            }));

            this.isLoadingMdfRequests = false;
        },

        hydrateRegisteredDeal(registeredDeal) {
            const agency = registeredDeal?.Agency;
            const opportunity = registeredDeal.Deal;
            const client = opportunity?.Account?.Client;
            const opportunityStatus = opportunity?.DealStatus;
            const submittedByUser = opportunity?.CreatedByUser;
            const primaryContact = opportunity?.Contact;
            const company = primaryContact?.Company;
            const agencySolutions = (registeredDeal.RegisteredDealAgencySolutions ?? []).map(
                (registeredDealAgencySolution) => {
                    const solution = registeredDealAgencySolution.AgencySolution;

                    return {
                        id: registeredDealAgencySolution.Id,
                        solutionId: solution.Id,
                        name: solution.Name,
                        unitCount: registeredDealAgencySolution.UnitCount,
                    };
                },
            );

            this.registeredDeal = {
                id: registeredDeal?.Id,
                name: registeredDeal?.Name,
                registrationNumber: registeredDeal?.RegistrationNumber,
                fundRequestId: registeredDeal?.FundRequestId,
                fundRequestName: registeredDeal?.FundRequestName,
                isApproved: registeredDeal?.IsApproved,
                reason: registeredDeal?.Reason,
                description: registeredDeal?.Description,
                amount: registeredDeal?.Value,
                projectedCloseDate: registeredDeal?.ProjectedClosingDate
                    ? new Date(registeredDeal.ProjectedClosingDate)
                    : null,
                agency: agency
                    ? {
                          id: agency.Id,
                          name: agency.Name,
                          logoImageUrl: agency.LogoImageUrl,
                      }
                    : null,
                client: client
                    ? {
                          id: client.Id,
                          name: client.Name,
                          imgUrl: client.LogoImageUrl,
                      }
                    : null,
                submittedByUser: submittedByUser
                    ? {
                          name: submittedByUser
                              ? `${submittedByUser.FirstName ?? ''} ${submittedByUser.LastName ?? ''}`.trim()
                              : null,
                          imgUrl: submittedByUser?.ProfileImageUrl,
                      }
                    : null,
                company: company
                    ? {
                          name: company?.Name,
                          imgUrl: company?.LogoUrl,
                      }
                    : null,
                primaryContact: primaryContact
                    ? {
                          name: primaryContact
                              ? `${primaryContact.FirstName ?? ''} ${primaryContact.LastName ?? ''}`.trim()
                              : null,
                          imgUrl: primaryContact?.ProfileImageUrl,
                      }
                    : null,
                opportunity: opportunity
                    ? {
                          id: opportunity.Id,
                          name: opportunity.Name,
                          stage: opportunityStatus
                              ? {
                                    name: opportunityStatus.Name,
                                    backgroundClass: this.$helpers.getContactStatusBackgroundClass(
                                        opportunityStatus.Color,
                                    ),
                                }
                              : null,
                      }
                    : null,
                agencySolutions,
            };
        },

        async edit() {
            this.editMode = true;

            await this.loadWidget();
        },

        async save() {
            this.submitted = true;
            EventBus.$emit(REGISTERED_DEAL_WIDGET_ATTEMPT_SAVE_EVENT);

            if (this.$v.$invalid) {
                this.scrollToError();
                return;
            }

            await this.saveRegisteredDeal();
        },

        close() {
            EventBus.$emit('close-widget', this.widget);
        },

        scrollToError() {
            setTimeout(() => {
                this.$refs.widget.scrollTo('.registered-deal-error');
            });
        },

        // Update handlers

        updateName(name) {
            this.registeredDeal.name = name;
        },

        async updateAgency(agency) {
            this.agencyId = agency?.id ?? null;
            this.registeredDeal.agency = agency ? { ...agency } : null;
            this.registeredDeal.agencySolutions = [];
            this.agencySolutions = [];

            await Promise.all([this.loadMdfRequests(), this.loadAgencySolutions()]);
        },

        updateFundRequest({ fundRequestId, fundRequestName }) {
            this.registeredDeal.fundRequestId = fundRequestId;
            this.registeredDeal.fundRequestName = fundRequestName;
        },

        updateRegistrationNumber(registrationNumber) {
            this.registeredDeal.registrationNumber = registrationNumber;
        },

        updateCloseDate(closeDate) {
            this.registeredDeal.projectedCloseDate = closeDate;
        },

        updateDescription(description) {
            this.registeredDeal.description = description;
        },

        updateIsApproved(isApproved) {
            this.registeredDeal.isApproved = isApproved;
            this.registeredDeal.reason = null;
        },

        updateReason(reason) {
            this.registeredDeal.reason = reason;
        },

        updateAmount(amount) {
            this.registeredDeal.amount = amount;
        },

        addSolution() {
            let id = 1;
            if (this.registeredDeal.agencySolutions?.length > 0) {
                const ids = this.registeredDeal.agencySolutions.map((solution) => solution.id);
                id = Math.max(...ids) + 1;
            }

            this.registeredDeal.agencySolutions.push({
                id,
                solutionId: null,
                name: null,
                unitCount: 1,
            });
        },

        deleteSolution(id) {
            const index = this.registeredDeal.agencySolutions.findIndex(
                (solution) => solution.id === id,
            );

            if (index > -1) {
                this.registeredDeal.agencySolutions.splice(index, 1);
            }
        },

        updateSolutionId({ agencySolutionId, solution }) {
            const agencySolution = this.registeredDeal.agencySolutions.find(
                (solution) => solution.id === agencySolutionId,
            );

            if (agencySolution) {
                agencySolution.solutionId = solution.id;
                agencySolution.name = solution.name;
            }
        },

        updateSolutionUnitCount({ agencySolutionId, unitCount }) {
            const agencySolution = this.registeredDeal.agencySolutions.find(
                (solution) => solution.id === agencySolutionId,
            );

            if (agencySolution) {
                agencySolution.unitCount = unitCount;
            }
        },
    },
};
</script>

<style>
.registered-deal-widget .registered-deal-widget-content {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.registered-deal-widget .registered-deal-widget-content h3 {
    margin-bottom: 0;
}

.registered-deal-widget .value-row {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.registered-deal-widget .input-container {
    margin-bottom: 0;
}

.registered-deal-widget .input-hint,
.registered-deal-widget .input-error {
    font-size: 12px;
}

.registered-deal-widget .input-hint {
    color: var(--color-text-subtle);
}

.registered-deal-widget .input-error {
    color: var(--color-text-accent-vividraspberry);
}

.registered-deal-widget label {
    font-weight: 500;
    margin-bottom: 0;
}

.registered-deal-widget label.label-error {
    color: var(--color-text-accent-vividraspberry);
}

.registered-deal-widget label.required::after {
    content: ' *';
    color: var(--color-text-accent-vividraspberry);
}

.registered-deal-widget .app-input.error input,
.registered-deal-widget .agency-search .multiselect.app-multiselect-new.invalid .multiselect__tags {
    border-width: 2px;
}

.registered-deal-widget .app-date-picker.error input {
    border-color: var(--color-text-accent-vividraspberry);
}

.registered-deal-widget .detail-icon-value-container {
    display: flex;
    align-items: center;
    gap: 8px;
}

.registered-deal-widget .detail-icon-value-container .detail-icon-container {
    display: flex;
    justify-content: center;
    align-items: center;
}

.registered-deal-widget .detail-icon-value-container .detail-icon-container .detail-icon {
    user-select: none;
}

.registered-deal-widget .detail-icon-value-container .detail-icon {
    width: 24px;
    height: 24px;
    border-radius: 50%;
}

.registered-deal-widget .divider {
    border-bottom: 1px solid var(--color-border-divider);
}

.registered-deal-widget .app-dropdown .v-select__slot {
    display: flex;
    align-items: center;
}

.registered-deal-widget .v-text-field .v-input__prepend-inner,
.registered-deal-widget .v-text-field .v-input__append-inner {
    align-self: auto;
}

.registered-deal-widget .app-select.v-input .v-input__append-inner {
    margin-top: 0;
}

.registered-deal-widget input,
.registered-deal-widget .app-dropdown .v-input__control > .v-input__slot,
.registered-deal-widget .agency-search .app-multiselect-new.multiselect {
    height: 40px;
}

.registered-deal-widget .agency-search input {
    height: auto;
}
</style>
